import { useCallback, useMemo } from "react";
import {
  EngagementSurveyReportGroupBy,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";

import ViewOptionsSidebar from "../sidebar/view-options-sidebar";
import EngagementSurveyGroupBySelector from "./engagement-survey-group-by-selector";
import EngagementSurveyReportFilterContainer from "./engagement-survey-report-filter-container";
import { getResponderKey, getResponderLabel } from "./helpers";
import {
  EngagementSurveyReportViewOptions,
  EngagmentSurveyGroupBySelectorItem,
} from "./types";

const EngagementSurveyReportViewOptionsSidebar = ({
  viewOptions,
  onUpdateViewOptions,
  onClose,
  responders,
  isLoading,
}: {
  viewOptions: EngagementSurveyReportViewOptions;
  onUpdateViewOptions: (
    opts: Partial<EngagementSurveyReportViewOptions>,
  ) => void;
  onClose: () => void;
  responders: EngagementSurveyReportResponderFragment[];
  isLoading: boolean;
}) => {
  const columnResponders = useMemo(
    () =>
      responders.filter(
        (r) =>
          ![
            EngagementSurveyRollupType.Organization,
            EngagementSurveyRollupType.Collapsed,
          ].includes(r.rollupType),
      ),
    [responders],
  );

  const handleChangeGroupBy = useCallback(
    (groupBy: EngagementSurveyReportGroupBy | null) => {
      onUpdateViewOptions({
        groupBy,
        hiddenColumns: [],
      });
    },
    [onUpdateViewOptions],
  );

  const handleChangeHiddenItems = useCallback(
    (newHiddenColumns: string[]) => {
      onUpdateViewOptions({ hiddenColumns: newHiddenColumns });
    },
    [onUpdateViewOptions],
  );

  const selectableGroupNames: EngagmentSurveyGroupBySelectorItem[] =
    useMemo(() => {
      return columnResponders.map((responder) => ({
        key: getResponderKey(responder),
        label: getResponderLabel(responder),
      }));
    }, [columnResponders]);

  return (
    <ViewOptionsSidebar onClose={onClose}>
      <div className="border-t p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Filters</div>
        </div>
        <div>
          <EngagementSurveyReportFilterContainer
            filters={viewOptions.filters.list}
            onUpdateFilters={(newFilters) =>
              onUpdateViewOptions({
                filters: { ...viewOptions.filters, list: newFilters },
              })
            }
          />
        </div>
      </div>

      <EngagementSurveyGroupBySelector
        groupBy={viewOptions.groupBy}
        onChangeGroupBy={handleChangeGroupBy}
        title="Group By"
        includeNoneOption={false}
        selectableItems={selectableGroupNames}
        hiddenItemKeys={viewOptions.hiddenColumns}
        onChangeHiddenItems={handleChangeHiddenItems}
        infoMessage="Columns that are not selected will be automatically grouped into
                one shared column."
        isLoading={isLoading}
      />
    </ViewOptionsSidebar>
  );
};

export default EngagementSurveyReportViewOptionsSidebar;
